
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Proforma, ProformaStatus, Cliente } from '@shared/types';
import { useUser } from '@/contexts/UserContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ExportDialog } from '@/components/ExportDialog';
import { Plus, Trash, Pencil, ChevronsUpDown, Download, Loader2, FileText, ArrowRightCircle, FileCheck } from 'lucide-react';
import * as XLSX from 'xlsx';

type EnrichedProforma = Proforma & { clienteNombre: string };
type SortKey = keyof EnrichedProforma | 'total';

const PROFORMA_COLUMNS = [
  { id: 'numero', name: 'Nº Proforma' },
  { id: 'clienteNombre', name: 'Cliente' },
  { id: 'fecha', name: 'Fecha' },
  { id: 'fechaVencimiento', name: 'Vencimiento' },
  { id: 'total', name: 'Total' },
  { id: 'estado', name: 'Estado' },
  { id: 'facturaGeneradaNumero', name: 'Factura Generada' },
];

const getStatusBadgeVariant = (status: ProformaStatus): 'success' | 'destructive' | 'default' | 'secondary' | 'warning' => {
  const variants: Record<ProformaStatus, 'success' | 'destructive' | 'default' | 'secondary' | 'warning'> = {
    'Aceptada': 'success',
    'Rechazada': 'destructive',
    'Pendiente': 'default',
    'Expirada': 'secondary',
    'Convertida': 'success',
  };
  return variants[status] || 'default';
};

export default function ProformasPage() {
  const navigate = useNavigate();
  const { fetchWithAuth } = useUser();
  const [proformas, setProformas] = useState<Proforma[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({ key: 'fecha', direction: 'desc' });
  const [isExportDialog, setExportDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [convertId, setConvertId] = useState<string | null>(null);
  const [convertPreview, setConvertPreview] = useState<any>(null);
  const [isConverting, setIsConverting] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [proformasRes, clientesRes] = await Promise.all([
        fetchWithAuth('/api/proformas'),
        fetchWithAuth('/api/clientes'),
      ]);
      if (!proformasRes.ok || !clientesRes.ok) throw new Error('No se pudieron obtener los datos');
      const [proformasData, clientesData] = await Promise.all([proformasRes.json(), clientesRes.json()]);
      setProformas(proformasData);
      setClientes(clientesData);
    } catch (error) {
      toast.error('Error al cargar datos', { description: error instanceof Error ? error.message : 'Error desconocido' });
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const enrichedProformas = useMemo(() => {
    const clienteMap = new Map(clientes.map(c => [c.id, c.nombre]));
    return proformas.map(p => ({
      ...p,
      clienteNombre: clienteMap.get(p.clienteId) || 'Cliente Desconocido',
    }));
  }, [proformas, clientes]);

  const sortedAndFiltered = useMemo(() => {
    const filtered = enrichedProformas.filter(p =>
      p.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.clienteNombre.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return [...filtered].sort((a, b) => {
      const valA = a[sortConfig.key] ?? '';
      const valB = b[sortConfig.key] ?? '';
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [enrichedProformas, searchTerm, sortConfig]);

  const handleSort = (key: SortKey) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const handleDeleteConfirm = () => {
    if (!deleteId) return;
    const proforma = proformas.find(p => p.id === deleteId);
    if (proforma?.estado === 'Convertida') {
      toast.error('No se puede eliminar una proforma convertida a factura');
      setDeleteId(null);
      return;
    }

    const promise = fetchWithAuth(`/api/proformas/${deleteId}`, { method: 'DELETE' }).then(res => {
      if (!res.ok) throw new Error('No se pudo eliminar la proforma');
    });

    toast.promise(promise, {
      loading: 'Eliminando proforma...',
      success: () => {
        fetchData();
        setDeleteId(null);
        return 'Proforma eliminada con éxito';
      },
      error: (err) => `Error: ${err.message}`,
    });
  };

  const handleConvertClick = async (proformaId: string) => {
    const proforma = proformas.find(p => p.id === proformaId);
    if (proforma?.estado === 'Convertida') {
      toast.info('Esta proforma ya fue convertida', {
        description: `Factura: ${proforma.facturaGeneradaNumero}`,
        action: {
          label: 'Ver factura',
          onClick: () => navigate(`/facturas/${proforma.facturaGeneradaId}/edit`)
        }
      });
      return;
    }
    if (proforma?.estado === 'Rechazada') {
      toast.error('No se puede convertir una proforma rechazada');
      return;
    }

    try {
      const res = await fetchWithAuth(`/api/proformas/${proformaId}/preview-conversion`);
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message);
      }
      const preview = await res.json();
      setConvertPreview(preview);
      setConvertId(proformaId);
    } catch (error) {
      toast.error('Error al preparar conversión', { description: error instanceof Error ? error.message : 'Error desconocido' });
    }
  };

  const handleConvertConfirm = async () => {
    if (!convertId) return;
    setIsConverting(true);

    try {
      const res = await fetchWithAuth(`/api/proformas/${convertId}/convertir`, { method: 'POST' });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message);
      }
      const result = await res.json();
      toast.success('Proforma convertida a factura', {
        description: `Factura ${result.factura.numero} creada exitosamente`,
        action: {
          label: 'Ver factura',
          onClick: () => navigate(`/facturas/${result.factura.id}/edit`)
        }
      });
      setConvertId(null);
      setConvertPreview(null);
      fetchData();
    } catch (error) {
      toast.error('Error al convertir', { description: error instanceof Error ? error.message : 'Error desconocido' });
    } finally {
      setIsConverting(false);
    }
  };

  const handleDownloadPDF = (proformaId: string) => {
    navigate(`/proformas/${proformaId}/edit?showPreview=true`);
  };

  const handleExport = (selectedHeaders: string[]) => {
    const dataToExport = sortedAndFiltered.map(p => {
      const row: { [key: string]: any } = {};
      selectedHeaders.forEach(header => {
        switch (header) {
          case 'Nº Proforma': row[header] = p.numero; break;
          case 'Cliente': row[header] = p.clienteNombre; break;
          case 'Fecha': row[header] = new Date(p.fecha).toLocaleDateString(); break;
          case 'Vencimiento': row[header] = p.fechaVencimiento ? new Date(p.fechaVencimiento).toLocaleDateString() : ''; break;
          case 'Total': row[header] = p.total; break;
          case 'Estado': row[header] = p.estado; break;
          case 'Factura Generada': row[header] = p.facturaGeneradaNumero || ''; break;
        }
      });
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(dataToExport, { header: selectedHeaders });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Proformas");
    XLSX.writeFile(wb, "proformas.xlsx");
    toast.success("Exportación completada");
  };

  const SortableHeader: React.FC<{ sortKey: SortKey, label: string, className?: string }> = ({ sortKey, label, className }) => (
    <TableHead onClick={() => handleSort(sortKey)} className={`cursor-pointer hover:bg-muted/50 transition-colors ${className}`}>
      <div className="flex items-center gap-2">{label} {sortConfig.key === sortKey && <ChevronsUpDown className="h-4 w-4"/>}</div>
    </TableHead>
  );

  const canConvert = (p: EnrichedProforma) => p.estado === 'Pendiente' || p.estado === 'Aceptada';
  const canDelete = (p: EnrichedProforma) => p.estado !== 'Convertida';
  const canEdit = (p: EnrichedProforma) => p.estado !== 'Convertida';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tighter">Proformas</h1>
          <p className="text-muted-foreground mt-1">Gestiona tus facturas proforma antes de convertirlas en facturas oficiales.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setExportDialog(true)} disabled={proformas.length === 0}>
            <Download className="mr-2 h-4 w-4" /> Exportar
          </Button>
          <Button onClick={() => navigate('/proformas/nuevo')}>
            <Plus className="mr-2 h-4 w-4" /> Nueva Proforma
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <Input
            placeholder="Buscar por número o cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader sortKey="numero" label="Nº" />
                  <SortableHeader sortKey="clienteNombre" label="Cliente" />
                  <SortableHeader sortKey="fecha" label="Fecha" />
                  <SortableHeader sortKey="total" label="Total" className="text-right" />
                  <SortableHeader sortKey="estado" label="Estado" />
                  <TableHead>Factura</TableHead>
                  <TableHead className="text-right pr-6">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={7} className="h-24 text-center"><Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" /></TableCell></TableRow>
                ) : sortedAndFiltered.length > 0 ? sortedAndFiltered.map((p) => (
                  <TableRow key={p.id} onDoubleClick={() => canEdit(p) && navigate(`/proformas/${p.id}/edit`)} className={`hover:bg-muted/50 ${canEdit(p) ? 'cursor-pointer' : 'opacity-75'}`}>
                    <TableCell className="font-medium">{p.numero}</TableCell>
                    <TableCell className="text-muted-foreground">{p.clienteNombre}</TableCell>
                    <TableCell className="text-muted-foreground">{new Date(p.fecha).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right font-mono">{p.total.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</TableCell>
                    <TableCell><Badge variant={getStatusBadgeVariant(p.estado)}>{p.estado}</Badge></TableCell>
                    <TableCell>
                      {p.facturaGeneradaNumero ? (
                        <Button variant="link" size="sm" className="p-0 h-auto" onClick={() => navigate(`/facturas/${p.facturaGeneradaId}/edit`)}>
                          <FileCheck className="w-4 h-4 mr-1" />{p.facturaGeneradaNumero}
                        </Button>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end items-center gap-1">
                        {canConvert(p) && (
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleConvertClick(p.id!)}} title="Convertir a factura">
                            <ArrowRightCircle className="w-4 h-4 text-primary" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDownloadPDF(p.id!)}} title="Descargar PDF">
                          <FileText className="w-4 h-4" />
                        </Button>
                        {canEdit(p) && (
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/proformas/${p.id}/edit`)}}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {canDelete(p) && (
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDeleteId(p.id!)}}>
                            <Trash className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">No has creado ninguna proforma todavía.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <ExportDialog
        open={isExportDialog}
        onOpenChange={setExportDialog}
        allHeaders={PROFORMA_COLUMNS.map(c => c.name)}
        onExport={handleExport}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirmar eliminación?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer. La proforma será eliminada permanentemente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Convert to Invoice Confirmation Dialog */}
      <AlertDialog open={!!convertId && !!convertPreview} onOpenChange={() => { setConvertId(null); setConvertPreview(null); }}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Convertir Proforma a Factura</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>Se creará una factura oficial a partir de esta proforma. Esta acción generará los datos VeriFactu necesarios para la AEAT.</p>
              {convertPreview && (
                <div className="bg-muted/50 p-4 rounded-lg space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Proforma:</span>
                    <span className="font-medium">{convertPreview.proforma.numero}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Nueva factura:</span>
                    <span className="font-medium text-primary">{convertPreview.facturaPreview.numero}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total:</span>
                    <span className="font-medium">{convertPreview.facturaPreview.total?.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span>
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isConverting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConvertConfirm} disabled={isConverting}>
              {isConverting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Convirtiendo...</> : 'Confirmar y Crear Factura'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
